import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, 
  message, Upload, Space, Typography, Card, Tag, Avatar
} from 'antd';
import { UploadOutlined, DeleteOutlined, UserOutlined, EditOutlined } from '@ant-design/icons';
import api from '../services/api';

const { TextArea } = Input;
const { Title } = Typography;
const BASE_UPLOAD_URL = 'https://www.businessmateworkflow.in/uploads/cms/';

const TestimonialsList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const statusColors = {
    approved: 'green',
    rejected: 'red',
    pending: 'orange'
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await api.get('/user/testimonials');
      setTestimonials(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'image',
        action: `${api.defaults.baseURL}/content/upload`,
    headers: {
      authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    fileList,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return false;
      }
      return true;
    },
    onChange: (info) => {
      setFileList(info.fileList);
      if (info.file.status === 'uploading') {
        setUploading(true);
        return;
      }
      if (info.file.status === 'done') {
        setUploading(false);
        if (info.file.response && info.file.response.success) {
          message.success('Image uploaded successfully!');
          form.setFieldsValue({
            profile_image: info.file.response.filename
          });
        }
      }
    },
    onRemove: () => {
      form.setFieldsValue({
        profile_image: null
      });
      setFileList([]);
    },
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingId) {
        await api.put(`/user/testimonials/${editingId}`, values);
        message.success('Testimonial updated successfully');
      } else {
        await api.post('/user/testimonials', values);
        message.success('Testimonial submitted for approval');
      }

      setVisible(false);
      form.resetFields();
      setFileList([]);
      setEditingId(null);
      fetchTestimonials();
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      ...record,
      is_active: record.is_active === 1
    });
    
    if (record.profile_image) {
      setFileList([{
        uid: '-1',
        name: record.profile_image,
        status: 'done',
        url: `${BASE_UPLOAD_URL}${record.profile_image}`
      }]);
    }
    
    setEditingId(record.id);
    setVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await api.delete(`/user/testimonials/${id}`);
      message.success('Testimonial deleted successfully');
      fetchTestimonials();
    } catch (error) {
      message.error('Failed to delete testimonial');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Image',
      dataIndex: 'profile_image',
      key: 'image',
      width: 80,
      render: (image) => (
        <Avatar 
          size={40} 
          src={image ? `${BASE_UPLOAD_URL}${image}` : null} 
          icon={!image && <UserOutlined />}
        />
      )
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status]}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            disabled={record.status === 'approved'}
          />
          <Button 
            icon={<DeleteOutlined />} 
            danger 
            onClick={() => handleDelete(record.id)}
            loading={loading}
          />
        </Space>
      )
    }
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>My Testimonials</Title>
        <Button 
          type="primary" 
          onClick={() => {
            form.resetFields();
            setFileList([]);
            setEditingId(null);
            setVisible(true);
          }}
        >
          Add New Testimonial
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={testimonials}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingId ? 'Edit Testimonial' : 'Add New Testimonial'}
        visible={visible}
        onOk={handleSubmit}
        onCancel={() => {
          setVisible(false);
          setFileList([]);
        }}
        confirmLoading={loading || uploading}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please input the title!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label="Person Name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>
{/* 
          <Form.Item
            name="designation"
            label="Designation"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="company"
            label="Company"
          >
            <Input />
          </Form.Item> */}

          <Form.Item
            name="message"
            label="Testimonial Message"
            rules={[{ required: true, message: 'Please input the message!' }]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="profile_image"
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item label="Profile Image">
            <Upload {...uploadProps} maxCount={1}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? 'Uploading...' : 'Click to Upload'}
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default TestimonialsList;